import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";

export default function ForgotPassword() {
  // Steps: 1 = Enter Email, 2 = Verify OTP, 3 = Reset Password
  const [step, setStep] = useState(1);

  // Email step
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // OTP step
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  // Password step
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const navigate = useNavigate();
  const emailInputRef = useRef(null);

  // Regex patterns
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const uppercaseRegex = /[A-Z]/;
  const numberRegex = /[0-9]/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

  // Email validation
  useEffect(() => {
    if (emailTouched) {
      if (!email.trim()) {
        setEmailError("Email address is required.");
      } else if (!emailRegex.test(email.trim())) {
        setEmailError("Please enter a valid email address.");
      } else {
        setEmailError("");
      }
    }
  }, [email, emailTouched]);

  // Password live validation
  useEffect(() => {
    setPasswordChecks({
      length: password.length >= 6,
      uppercase: uppercaseRegex.test(password),
      number: numberRegex.test(password),
      specialChar: specialCharRegex.test(password),
    });

    if (passwordTouched) {
      const allValid =
        password.length >= 6 &&
        uppercaseRegex.test(password) &&
        numberRegex.test(password) &&
        specialCharRegex.test(password);
      setPasswordError(allValid ? "" : "Password does not meet the criteria.");
    }

    if (confirmPasswordTouched) {
      if (confirmPassword !== password) {
        setConfirmPasswordError("Passwords do not match.");
      } else {
        setConfirmPasswordError("");
      }
    }
  }, [password, confirmPassword, passwordTouched, confirmPasswordTouched]);

  const isEmailValid = emailRegex.test(email.trim());
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const isConfirmPasswordValid = confirmPassword === password && confirmPassword.length > 0;

  // Step 1: Check if email is registered
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setEmailTouched(true);
    setFormError("");

    if (!isEmailValid) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setIsCheckingEmail(true);

    try {
      const response = await authService.forgotPasswordCheckEmail(email.trim());

      if (response.success) {
        // Email is registered, move to OTP step
        setStep(2);
        setFormError("");
        // Auto-send OTP
        await sendOtp();
      } else {
        setFormError(response.error || "This email is not registered.");
      }
    } catch (error) {
      setFormError(error.message || "Failed to verify email. Please try again.");
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Send OTP to the email
  const sendOtp = async () => {
    setIsSendingOtp(true);
    setOtpError("");

    try {
      const response = await authService.sendEmailOTP(email.trim());
      if (response.success) {
        setOtpSent(true);
        setOtpError("");
      } else {
        setOtpError(response.error || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setOtpError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError("");

    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }

    setIsVerifyingOtp(true);

    try {
      const response = await authService.verifyEmailOTP(email.trim(), otp);
      if (response.success) {
        setStep(3);
        setFormError("");
      } else {
        setOtpError(response.error || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setOtpError(error.message || "Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordTouched(true);
    setConfirmPasswordTouched(true);
    setFormError("");

    if (!isPasswordValid) {
      setFormError("Password does not meet all criteria.");
      return;
    }

    if (!isConfirmPasswordValid) {
      setFormError("Passwords do not match.");
      return;
    }

    setIsResetting(true);

    try {
      const response = await authService.forgotPasswordReset(
        email.trim(),
        password,
        confirmPassword
      );

      if (response.success) {
        setSuccessMessage(response.message || "Password has been reset successfully!");
        setFormError("");
        // Redirect to signin after 2 seconds
        setTimeout(() => {
          navigate("/signin");
        }, 2000);
      } else {
        setFormError(response.error || "Failed to reset password. Please try again.");
      }
    } catch (error) {
      setFormError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setOtp("");
    setOtpError("");
    setOtpSent(false);
    await sendOtp();
  };

  return (
    <div className="container py-5" style={{ maxWidth: 480 }}>
      <h2
        className="fw-bold mb-2 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", color: "#6B46C1" }}
      >
        Forgot Password
      </h2>

      {/* Step Indicator */}
      <div className="d-flex justify-content-center align-items-center gap-2 mb-4">
        <StepBadge number={1} label="Email" active={step === 1} completed={step > 1} />
        <div style={{ width: 30, height: 2, background: step > 1 ? "#6B46C1" : "#dee2e6" }}></div>
        <StepBadge number={2} label="Verify" active={step === 2} completed={step > 2} />
        <div style={{ width: 30, height: 2, background: step > 2 ? "#6B46C1" : "#dee2e6" }}></div>
        <StepBadge number={3} label="Reset" active={step === 3} completed={false} />
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success text-center" role="alert">
          <i className="bi bi-check-circle-fill me-2"></i>
          {successMessage}
          <div className="small mt-1 text-muted">Redirecting to Sign In...</div>
        </div>
      )}

      {/* Form Error */}
      {formError && !successMessage && (
        <div className="alert alert-danger" role="alert">
          {formError}
        </div>
      )}

      {/* Step 1: Enter Email */}
      {step === 1 && !successMessage && (
        <form noValidate onSubmit={handleCheckEmail}>
          <p className="text-muted text-center mb-4">
            Enter your registered email address to reset your password.
          </p>

          <div className="mb-3">
            <label htmlFor="email" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
              Email Address <span className="text-danger">*</span>
            </label>
            <input
              ref={emailInputRef}
              type="email"
              id="email"
              className={`form-control ${emailTouched && emailError ? "is-invalid" : emailTouched && isEmailValid ? "is-valid" : ""}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFormError("");
              }}
              onBlur={() => setEmailTouched(true)}
              disabled={isCheckingEmail}
              required
              autoComplete="email"
              autoFocus
            />
            {emailTouched && emailError && (
              <div className="invalid-feedback d-block">{emailError}</div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 fw-semibold py-2"
            disabled={!isEmailValid || isCheckingEmail}
          >
            {isCheckingEmail ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Checking...
              </>
            ) : (
              "Continue"
            )}
          </button>

          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-link text-decoration-none"
              onClick={() => navigate("/signin")}
              style={{ color: "#6B46C1" }}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to Sign In
            </button>
          </div>
        </form>
      )}

      {/* Step 2: Verify OTP */}
      {step === 2 && !successMessage && (
        <form noValidate onSubmit={handleVerifyOtp}>
          <p className="text-muted text-center mb-4">
            We've sent a verification code to <strong>{email}</strong>. Enter it below to continue.
          </p>

          <div className="mb-3">
            <label htmlFor="otp" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
              Verification Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="otp"
              className={`form-control text-center ${otpError ? "is-invalid" : ""}`}
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                setOtp(value);
                setOtpError("");
              }}
              maxLength={6}
              autoComplete="one-time-code"
              disabled={isVerifyingOtp}
              autoFocus
              style={{ fontSize: "1.2rem", letterSpacing: "0.3em" }}
            />
            {otpError && (
              <div className="invalid-feedback d-block">{otpError}</div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 fw-semibold py-2"
            disabled={otp.length !== 6 || isVerifyingOtp}
          >
            {isVerifyingOtp ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </button>

          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-link text-decoration-none small"
              onClick={handleResendOtp}
              disabled={isSendingOtp}
              style={{ color: "#6B46C1" }}
            >
              {isSendingOtp ? "Sending..." : "Didn't receive the code? Resend OTP"}
            </button>
          </div>

          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-link text-decoration-none small text-muted"
              onClick={() => { setStep(1); setOtp(""); setOtpError(""); setOtpSent(false); setFormError(""); }}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Change email address
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Reset Password */}
      {step === 3 && !successMessage && (
        <form noValidate onSubmit={handleResetPassword}>
          <p className="text-muted text-center mb-4">
            Email verified! Create your new password below.
          </p>

          <div className="alert alert-success small py-2 mb-3 text-center">
            <i className="bi bi-check-circle-fill me-1"></i>
            Email <strong>{email}</strong> verified successfully
          </div>

          {/* New Password */}
          <div className="mb-3 position-relative">
            <label htmlFor="newPassword" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
              New Password <span className="text-danger">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="newPassword"
              className={`form-control ${passwordTouched && passwordError ? "is-invalid" : ""}`}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setPasswordTouched(true)}
              disabled={isResetting}
              required
              autoComplete="new-password"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
              style={{ zIndex: 5, borderRadius: "0 0.375rem 0.375rem 0", marginTop: "15px" }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <i className="bi bi-eye-slash-fill"></i>
              ) : (
                <i className="bi bi-eye-fill"></i>
              )}
            </button>
            {passwordTouched && passwordError && (
              <div className="invalid-feedback d-block">{passwordError}</div>
            )}
          </div>

          {/* Password criteria */}
          <div className="mb-3 p-3 border rounded bg-light" style={{ fontSize: 14, userSelect: "none" }}>
            <ul className="mb-0 ps-3">
              <PasswordLine isValid={passwordChecks.length} text="Minimum 6 characters" />
              <PasswordLine isValid={passwordChecks.uppercase} text="At least 1 uppercase letter" />
              <PasswordLine isValid={passwordChecks.number} text="At least 1 number" />
              <PasswordLine isValid={passwordChecks.specialChar} text="At least 1 special character (!@#$%^&*)" />
            </ul>
          </div>

          {/* Confirm Password */}
          <div className="mb-4 position-relative">
            <label htmlFor="confirmPassword" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
              Confirm Password <span className="text-danger">*</span>
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              className={`form-control ${confirmPasswordTouched && confirmPasswordError ? "is-invalid" : ""}`}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setConfirmPasswordTouched(true)}
              disabled={isResetting}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
              style={{ zIndex: 5, borderRadius: "0 0.375rem 0.375rem 0", marginTop: "15px" }}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? (
                <i className="bi bi-eye-slash-fill"></i>
              ) : (
                <i className="bi bi-eye-fill"></i>
              )}
            </button>
            {confirmPasswordTouched && confirmPasswordError && (
              <div className="invalid-feedback d-block">{confirmPasswordError}</div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 fw-semibold py-2"
            disabled={!isPasswordValid || !isConfirmPasswordValid || isResetting}
          >
            {isResetting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>
      )}
    </div>
  );
}

function StepBadge({ number, label, active, completed }) {
  const bg = completed
    ? "#28a745"
    : active
    ? "#6B46C1"
    : "#dee2e6";
  const color = completed || active ? "#fff" : "#6c757d";

  return (
    <div className="text-center">
      <div
        className="d-flex align-items-center justify-content-center rounded-circle mx-auto"
        style={{
          width: 32,
          height: 32,
          background: bg,
          color: color,
          fontSize: "0.85rem",
          fontWeight: 600,
          transition: "all 0.3s ease",
        }}
      >
        {completed ? <i className="bi bi-check-lg"></i> : number}
      </div>
      <small
        className="d-block mt-1"
        style={{
          color: active ? "#6B46C1" : completed ? "#28a745" : "#6c757d",
          fontWeight: active ? 600 : 400,
          fontSize: "0.75rem",
        }}
      >
        {label}
      </small>
    </div>
  );
}

function PasswordLine({ isValid, text }) {
  return (
    <li
      className="d-flex align-items-center"
      style={{ color: isValid ? "#28a745" : "#dc3545", userSelect: "none" }}
    >
      <i className={`bi me-2 ${isValid ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
      {text}
    </li>
  );
}
